import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { CustomExteriorColorOptionsView, SetManualExteriorColorBody } from "@slfinrtl/ui-bff-routes"
import { getCustomExteriorColorOptionsRoute, setManualExteriorColorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import type { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { propertiesOf } from "src/common/property"
import { getMarketLanguageTranslations } from "src/common/translations"
import { useBff, useBffDynamic } from "src/hooks"
import type { CustomExteriorColorContainerProps } from "./CustomExteriorColorContainer"
import type { CustomExteriorColorProps } from "./view/CustomExteriorColor"

export const messages = defineMessages({
	hexCode: {
		defaultMessage: "Color Shade",
		id: "colors.exteriorColor.customExteriorColorModal.hexCode",
	},
	name: {
		defaultMessage: "Color Name",
		id: "colors.exteriorColor.customExteriorColorModal.name",
	},
	selectCatalogExteriorColor: {
		defaultMessage: "Select Catalog Exterior Color",
		id: "colors.exteriorColor.customExteriorColorModal.selectCatalogExteriorColor",
	},
	tag: {
		defaultMessage: "Color Group",
		id: "colors.exteriorColor.customExteriorColorModal.tag",
	},
	type: {
		defaultMessage: "Color Type",
		id: "colors.exteriorColor.customExteriorColorModal.type",
	},
})

export interface CustomExteriorColorForm {
	hexCode: string
	name: Record<string, string>
	// Usually we use empty string as "empty" default value, because undefined can cause problems with React-Hook-Forms https://react-hook-form.com/api/usecontroller#:~:text=undefined%20is%20not%20a%20valid%20value
	tagKey: string
	// But for color type we use RadioGroupField from retail-ui-kit which doesn't work with empty string as default value
	typeKey: string | undefined
}

export const useCustomExteriorColor = ({
	currentExteriorColor,
	inventoryId,
	onExteriorColorCreated,
	showCatalogExteriorColorContainer,
}: CustomExteriorColorContainerProps): {
	form: UseFormReturn<CustomExteriorColorForm>
	onSubmit: SubmitHandler<CustomExteriorColorForm>
	props: CustomExteriorColorProps | undefined
} => {
	const { availableLocales } = useDealerContext()

	const [allOptions, setAllOptions] = useState<CustomExteriorColorOptionsView>()

	const getCustomExteriorColorOptions = useBff(
		getCustomExteriorColorOptionsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {},
		},
		"inline",
	)

	useEffect(() => {
		getCustomExteriorColorOptions().then(setAllOptions)
	}, [getCustomExteriorColorOptions])

	const form = useForm<CustomExteriorColorForm>({
		defaultValues: {
			hexCode: currentExteriorColor?.hexCode ?? "",
			name: getMarketLanguageTranslations(availableLocales, currentExteriorColor?.name ?? {}),
			tagKey: currentExteriorColor?.tagKey ?? "",
			typeKey: currentExteriorColor?.typeKey ?? undefined,
		},
	})

	const setManualExteriorColor = useBffDynamic(setManualExteriorColorRoute, "inline")

	const [hasSubmitFailed, setHasSubmitFailed] = useState(false)
	const onSubmit: SubmitHandler<CustomExteriorColorForm> = async (formState) => {
		const setManualExteriorColorBody: SetManualExteriorColorBody = {
			hexCode: formState.hexCode,
			name: getMarketLanguageTranslations(availableLocales, formState.name),
			tagKey: formState.tagKey,
			typeKey: formState.typeKey!, // color type is required field, so we're sure it's set at this point
		}

		await setManualExteriorColor({
			body: setManualExteriorColorBody,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
			},
		})
			.then(() =>
				onExteriorColorCreated({
					optionCode: null,
					...setManualExteriorColorBody,
				}),
			)
			.catch(() => setHasSubmitFailed(true))
	}

	const { formatDisplayName, formatMessage } = useIntl()

	return {
		form,
		onSubmit,
		props: allOptions && {
			hasSubmitFailed,
			hexCode: {
				glossy: true,
				label: formatMessage(messages.hexCode),
				name: customExteriorColorFormProperties.hexCode._path,
				required: true,
			},
			isSubmitting: form.formState.isSubmitting,
			name: availableLocales.map((languageTag) => ({
				label: `${formatMessage(messages.name)} - ${formatDisplayName(languageTag, {
					type: "language",
				})}`,
				name: `${customExteriorColorFormProperties.name._path}.${languageTag}`,
				required: true,
				type: "text",
				validate: (value: string | number) =>
					value.toString() !== value.toString().toUpperCase() ||
					formatMessage(commonMessages.onlyCapitalLetters),
			})),
			setHasSubmitFailed,
			showCatalogExteriorColorContainer,
			tag: {
				label: formatMessage(messages.tag),
				name: customExteriorColorFormProperties.tagKey._path,
				options: allOptions.tags.map((tag) => ({
					hexCode: tag.hexCode,
					label: tag.name,
					value: tag.key,
				})),
				required: true,
			},
			type: {
				label: formatMessage(messages.type),
				name: customExteriorColorFormProperties.typeKey._path,
				options: allOptions.types.map((type) => ({
					label: type.name,
					value: type.key,
				})),
				required: true,
			},
		},
	}
}

const customExteriorColorFormProperties = propertiesOf<CustomExteriorColorForm>()
